import { useEffect, useState } from 'react'
import { imageCache } from '@services/imageCache'
import { useAppStore } from '@store/appStore'
import { toBlobUrl } from '@/config'

interface ImageDimensions {
  imageWidth: number
  imageHeight: number
  displayWidth: number
  displayHeight: number
}

interface UseImageLoaderParams {
  imagePath: string
  showDebugOverlay: boolean
  currentCamera: any
  currentSectionFramesPath: string | null
  cameras: any[]
}

interface UseImageLoaderResult {
  imageLoaded: boolean
  setImageLoaded: (loaded: boolean) => void
}

/**
 * Hook to handle image loading with caching and dimension calculation
 */
export function useImageLoader({
  imagePath,
  showDebugOverlay,
  currentCamera,
  currentSectionFramesPath,
  cameras
}: UseImageLoaderParams): UseImageLoaderResult {
  const [imageLoaded, setImageLoaded] = useState(false)
  const setImageDimensions = useAppStore((state) => state.setImageDimensions)
  const markFrameMissing = useAppStore((state) => state.markFrameMissing)

  useEffect(() => {
    // Clear warm-start frame once real cameras are loaded
    if (cameras.length > 0 && currentCamera) {
      useAppStore.getState().setWarmStartFrame(null)
    }
    
    if (!currentCamera) {
      console.log('⚠️ [NewModeViewer] No current camera, skipping image load')
      return
    }
    
    if (!currentSectionFramesPath) {
      console.log('⚠️ [NewModeViewer] currentSectionFramesPath not set yet, skipping image load')
      return
    }
    
    setImageLoaded(false)

    const cachedImage = imageCache.get(imagePath)
    if (cachedImage) {
      console.log(`✅ [Image] Using cached image: ${imagePath.split('/').pop()}`)
      
      // Set dimensions from cached image
      const naturalWidth = cachedImage.naturalWidth
      const naturalHeight = cachedImage.naturalHeight
      const naturalAspect = naturalWidth / naturalHeight
      
      const viewportHeight = showDebugOverlay ? window.innerHeight * 0.8 : window.innerHeight
      const displayHeight = viewportHeight
      const displayWidth = displayHeight * naturalAspect
      
      console.log(`📐 [ImageDimensions] From cache: ${naturalWidth}×${naturalHeight}, Display: ${displayWidth}×${displayHeight}`)
      setImageDimensions({
        imageWidth: naturalWidth,
        imageHeight: naturalHeight,
        displayWidth,
        displayHeight
      })
      
      setImageLoaded(true)
      return
    }

    console.log(`🖼️ [Image] Loading from: ${imagePath}`)
    
    const img = new Image()
    img.onload = () => {
      const naturalWidth = img.naturalWidth
      const naturalHeight = img.naturalHeight
      const naturalAspect = naturalWidth / naturalHeight

      console.log(`✅ [Image] Loaded successfully: ${naturalWidth}×${naturalHeight}`)

      // Cache the loaded image immediately for animation validation
      imageCache.addLoadedImage(imagePath, img)

      // Image viewer height depends on debug overlay state
      const viewportHeight = showDebugOverlay ? window.innerHeight * 0.8 : window.innerHeight

      // Always fill viewport height (frame may extend beyond viewport width)
      const displayHeight = viewportHeight
      const displayWidth = displayHeight * naturalAspect

      console.log(`📐 [ImageDimensions] Natural: ${naturalWidth}×${naturalHeight}, Display: ${displayWidth}×${displayHeight}`)
      setImageDimensions({
        imageWidth: naturalWidth,
        imageHeight: naturalHeight,
        displayWidth,
        displayHeight
      })
      setImageLoaded(true)
    }

    img.onerror = () => {
      console.error(`❌ [Image] Failed to load: ${imagePath}`)
      setImageLoaded(false)
      if (currentCamera?.frame_file) {
        markFrameMissing(currentCamera.frame_file)
      }
    }
    img.src = toBlobUrl(imagePath)
  }, [imagePath, showDebugOverlay, currentCamera, currentSectionFramesPath, cameras.length, setImageDimensions, markFrameMissing])

  return { imageLoaded, setImageLoaded }
}

/**
 * Calculate display dimensions based on camera intrinsics and debug overlay state
 */
export function calculateDisplayDimensions(
  cameraFrameWidth: number,
  cameraFrameHeight: number,
  showDebugOverlay: boolean
): { displayWidth: number; displayHeight: number } {
  const displayHeight = showDebugOverlay ? window.innerHeight * 0.8 : window.innerHeight
  const frameAspectRatio = cameraFrameWidth / cameraFrameHeight
  const displayWidth = displayHeight * frameAspectRatio
  return { displayWidth, displayHeight }
}
